'use client';

/**
 * Browser-side error reporter. Sends client errors to /api/errors/client,
 * which logs them via @wtc/logger → #Hub Errors Slack channel.
 *
 * Client-safe: no server imports. Uses the session cookie (same-origin), so no
 * token plumbing. Never throws — reporting must not create new errors.
 */

import { isIgnoredClientError } from '@/lib/observability/client-error-schema';

export interface ClientErrorReport {
  message: string;
  name?: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  digest?: string;
  severity?: 'error' | 'fatal';
  context?: Record<string, unknown>;
}

export function reportClientError(report: ClientErrorReport): void {
  try {
    if (typeof window === 'undefined') return;
    if (isIgnoredClientError(report.message)) return;
    const payload = JSON.stringify({
      ...report,
      url: report.url ?? window.location.href,
    });
    // keepalive lets the request survive a navigation / page unload.
    void fetch('/api/errors/client', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* swallow — reporting failures must be silent */
    });
  } catch {
    /* never throw from the reporter */
  }
}
